import React, { useEffect, useMemo, useRef, useState } from "react";
import { searchCards } from "../api/cards";

export interface CanonicalCardOption {
  game: string;
  provider?: string;
  cardId: string;
  providerSetId?: string;
  cardName: string;
  setName?: string;
  cardNumber?: string;
  normalizedCardNumber?: string;
  rarity?: string;
  language?: string;
  finish?: string;
  layout?: string;
  releaseDate?: string | null;
  imageUrl?: string;
  imageUrlSmall?: string;
  imageUrlLarge?: string;
  imageUrlBack?: string;
}

interface CardAutocompleteInputProps {
  game: string;
  value: string;
  onValueChange: (value: string) => void;
  selectedCard: CanonicalCardOption | null;
  onSelectCard: (card: CanonicalCardOption | null) => void;
  placeholder?: string;
  id?: string;
  required?: boolean;
}

export default function CardAutocompleteInput({
  game,
  value,
  onValueChange,
  selectedCard,
  onSelectCard,
  placeholder = "Search card name",
  id,
  required = false,
}: CardAutocompleteInputProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([] as CanonicalCardOption[]);
  const [error, setError] = useState("");
  const rootRef = useRef(null as HTMLDivElement | null);

  const normalizeGame = (input: string): string => String(input || "").trim().toLowerCase();
  const isSupportedGame = useMemo(() => {
    const normalized = normalizeGame(game);
    return (
      normalized === "pokemon" ||
      normalized === "pokémon" ||
      normalized === "magic: the gathering" ||
      normalized === "mtg" ||
      normalized === "magic" ||
      normalized === "yu-gi-oh!" ||
      normalized === "yu-gi-oh" ||
      normalized === "yugioh"
    );
  }, [game]);

  const canSearch = useMemo(() => value.trim().length >= 2 && Boolean(game), [value, game]);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (!canSearch || !isSupportedGame) {
      setResults([]);
      setLoading(false);
      setError("");
      return;
    }

    let active = true;
    const timeout = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const data = await searchCards({ game, q: value.trim(), limit: 8 });
        if (!active) return;
        setResults(Array.isArray(data) ? data : []);
      } catch (err: any) {
        if (!active) return;
        setResults([]);
        setError(err?.message || "Unable to search cards");
      } finally {
        if (active) {
          setLoading(false);
          setOpen(true);
        }
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timeout);
    };
  }, [canSearch, game, value, isSupportedGame]);

  const handleSelect = (item: CanonicalCardOption) => {
    onSelectCard(item);
    onValueChange(item.cardName);
    setOpen(false);
  };

  const onInputChange = (nextValue: string) => {
    onValueChange(nextValue);
    if (selectedCard && selectedCard.cardName !== nextValue) {
      onSelectCard(null);
    }
  };

  return (
    <div className="card-autocomplete" ref={rootRef}>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e: any) => onInputChange(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
      />
      {open && (
        <div className="card-autocomplete-menu">
          {Boolean(game) && !isSupportedGame && (
            <div className="card-autocomplete-note">Canonical suggestions for this game are coming soon.</div>
          )}
          {!canSearch && <div className="card-autocomplete-note">Type at least 2 characters</div>}
          {canSearch && isSupportedGame && loading && <div className="card-autocomplete-note">Searching...</div>}
          {canSearch && isSupportedGame && !loading && error && <div className="card-autocomplete-note">{error}</div>}
          {canSearch && isSupportedGame && !loading && !error && results.length === 0 && (
            <div className="card-autocomplete-note">No cards found</div>
          )}
          {isSupportedGame && results.map((item: any) => (
            <button
              type="button"
              key={`${item.game}:${item.cardId}`}
              className="card-autocomplete-item"
              onClick={() => handleSelect(item)}
            >
              <span className="card-autocomplete-name">{item.cardName}</span>
              <span className="card-autocomplete-meta">
                {[item.setName, item.cardNumber, item.rarity].filter(Boolean).join(" • ")}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
